import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Data, Router, RouterLink } from '@angular/router';
import { InlineInfoComponent } from '@catch/general-ui';
import { MarkdownViewerComponent } from '@catch/markdown-viewer';
import { TranslocoPipe } from '@jsverse/transloco';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { HeaderTagsComponent } from '../../snippets/header-tags/header-tags.component';
import { ColorService } from '../../../services/color.service';
import {JobDetail} from '../../../interfaces/job-detail';
import {FontawesomeService} from '../../../services/fontawesome.service';
import {EmbedService} from '../../../services/embed.service';
import {RefService} from '../../../services/ref.service';
import {Observable, zip} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {SEOService} from '../../../services/seo.service';
import {GoogleJobPostingService} from '../../../services/google-job-posting.service';
import {CompanyInfo} from '../../../interfaces/company-info';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-job',
  templateUrl: './job.component.html',
  styleUrls: ['./job.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    AsyncPipe,
    TranslocoPipe,
    RouterLink,
    HeaderTagsComponent,
    InlineInfoComponent,
    MarkdownViewerComponent,
    FaIconComponent
],
})
export class JobComponent implements OnInit {
  private readonly googleJobsService = inject(GoogleJobPostingService);
  private readonly refService = inject(RefService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly seoService = inject(SEOService);
  public readonly colorService = inject(ColorService);
  public readonly embedService = inject(EmbedService);
  public readonly fontawesomeService = inject(FontawesomeService);

  job$: Observable<JobDetail>;
  companyInfo$: Observable<CompanyInfo>;

  ngOnInit(): void {
    this.job$ = this.route.data.pipe(map((data: Data) => data['job']));
    this.companyInfo$ = this.route.data.pipe(map((data: Data) => data['companyInfo']));
    this.companyInfo$.pipe(untilDestroyed(this)).subscribe({
      next: (company: CompanyInfo) => {
        this.seoService.updateImage(company.logoURL);
      },
    });
    zip(this.job$, this.companyInfo$)
      .pipe(untilDestroyed(this))
      .subscribe(([job, companyInfo]) => {
        this.googleJobsService.setStructuredData(job, companyInfo);
      });
    this.job$
      .pipe(
        tap((job: JobDetail) => {
          this.colorService.setDesign(job.companyID);
          this.seoService.setWebsite(job.website);
          this.refService.countJobClick(job.companyID, job.id);
        }),
        switchMap((job: JobDetail) => this.seoService.updateJobPageSeo(job)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  get routerUrlNoQueryParams() {
    return this.router.url.match(/^([^?]*)(\?.+)?$/)[1];
  }
}
